/**
 * DR Pack Picker
 * Instance-scoped pack quantity picker with isolated cart forms.
 *
 * Each picker root contains:
 * - JSON config (data-dr-pack-config)
 * - Radio inputs for pack selection
 * - Hidden variant ID input
 * - Price strip elements (legacy + coupon pill modes)
 */
(function() {
  'use strict';

  function initPicker(root) {
    if (root.dataset.initialized === '1') {
      return;
    }
    root.dataset.initialized = '1';

    var configEl = root.querySelector('[data-dr-pack-config]');
    if (!configEl) {
      console.warn('DR Pack Picker: Missing config element');
      return;
    }

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      console.warn('DR Pack Picker: Invalid JSON config', e);
      return;
    }

    var packs = config.packs || {};
    var currencyHtml = config.currencyHtml || '';
    var isBundlePdp = !!config.isBundlePdp;
    var showPack1Coupon = !!config.showPack1Coupon;
    var form = root.querySelector('form.dr-pack-picker__form');
    var idInput = form ? form.querySelector('input[name="id"]') : null;
    var radios = root.querySelectorAll('.dr-pack-option__radio');
    var legacyStripEl = root.querySelector('[data-dr-pack-price-legacy]');
    var couponStripEl = root.querySelector('[data-dr-pack-price-coupon]');
    var oldEl = root.querySelector('[data-dr-pack-old]');
    var newEl = root.querySelector('[data-dr-pack-new]');
    var discountEl = root.querySelector('[data-dr-pack-discount]');
    var separatorEl = root.querySelector('[data-dr-pack-separator]');
    var couponRegularEl = root.querySelector('[data-dr-pack-regular]');
    var couponAmountEl = root.querySelector('[data-dr-pack-coupon]');
    var couponUnitEl = root.querySelector('[data-dr-pack-unit]');
    var retractableEl = root.querySelector('[data-dr-pack-retractable]');
    var retractableGiftEl = root.querySelector('[data-dr-pack-retractable-gift]');
    var retractableGiftTextEl = root.querySelector('[data-dr-pack-retractable-gift-text]');
    var bonusThreshold1 = config.bonusThreshold1 ?? 80000;
    var bonusThreshold2 = config.bonusThreshold2 ?? 120000;
    var bonusThreshold3 = config.bonusThreshold3 ?? 160000;
    var freeGiftEnabled = config.freeGiftEnabled === true;
    var bonusTier1Enabled = config.bonusTier1Enabled === true;
    var bonusTier2Enabled = config.bonusTier2Enabled === true;
    var bonusTier3Enabled = config.bonusTier3Enabled === true;
    var oldPriceNumClass = 'dr-pack-picker__price-old-num';
    var isSingleMode = root.classList.contains('dr-pack-picker--single');

    if (!idInput || radios.length === 0) {
      console.warn('DR Pack Picker: Missing required elements');
      return;
    }

    function usesCouponStrip(packKey) {
      return isBundlePdp || showPack1Coupon || packKey !== '1';
    }

    function setStripMode(packKey) {
      var couponMode = usesCouponStrip(packKey);
      if (legacyStripEl) {
        legacyStripEl.classList.toggle('is-hidden', couponMode);
      }
      if (couponStripEl) {
        couponStripEl.classList.toggle('is-hidden', !couponMode);
      }
    }

    function getGiftText(totalPrice) {
      var giftCount = freeGiftEnabled && totalPrice > 0 ? 1 : 0;
      if (bonusTier1Enabled && totalPrice >= bonusThreshold1) giftCount += 1;
      if (bonusTier2Enabled && totalPrice >= bonusThreshold2) giftCount += 1;
      if (bonusTier3Enabled && totalPrice >= bonusThreshold3) giftCount += 1;

      if (giftCount === 1) return 'מתנה בגודל מלא (בסל)';
      if (giftCount === 2) return 'שתי מתנות בגודל מלא (בסל)';
      if (giftCount === 3) return 'שלוש מתנות בגודל מלא (בסל)';
      if (giftCount === 4) return 'ארבע מתנות בגודל מלא (בסל)';
      return '';
    }

    function updateGiftBanner(totalPrice) {
      if (!retractableEl) return;

      var giftText = getGiftText(totalPrice);
      var showGiftRow = giftText.length > 0;

      if (showGiftRow) {
        retractableEl.classList.add('is-active');
        retractableEl.setAttribute('aria-hidden', 'false');

        if (retractableGiftEl) {
          retractableGiftEl.classList.remove('is-hidden');
          if (retractableGiftTextEl) {
            retractableGiftTextEl.innerHTML = giftText;
          }
        }
      } else {
        retractableEl.classList.remove('is-active');
        retractableEl.setAttribute('aria-hidden', 'true');
        if (retractableGiftEl) {
          retractableGiftEl.classList.add('is-hidden');
        }
      }
    }

    function applyPack(packKey) {
      var pack = packs[packKey];
      if (!pack) {
        return;
      }

      idInput.value = pack.variantId;
      setStripMode(packKey);

      if (usesCouponStrip(packKey)) {
        if (couponRegularEl) {
          couponRegularEl.textContent = pack.regularText || String(Math.round(parseFloat(pack.newText) || 0));
        }
        if (couponAmountEl) {
          couponAmountEl.textContent = pack.couponText || '';
        }
        if (couponUnitEl) {
          couponUnitEl.textContent = pack.couponUnitPriceText != null ? String(pack.couponUnitPriceText) : '';
        }
      } else if (newEl) {
        newEl.innerHTML = currencyHtml + (pack.newText || '');

        if (oldEl) {
          if (pack.oldText && pack.oldText !== pack.newText) {
            oldEl.innerHTML = currencyHtml + '<span class="' + oldPriceNumClass + '">' + pack.oldText + '</span>';
            oldEl.classList.remove('is-hidden');
          } else {
            oldEl.innerHTML = '';
            oldEl.classList.add('is-hidden');
          }
        }

        var hasDiscount = pack.discountText && pack.discountText.length > 0;
        if (discountEl) {
          if (hasDiscount) {
            discountEl.textContent = pack.discountText;
            discountEl.classList.remove('is-hidden');
          } else {
            discountEl.textContent = '';
            discountEl.classList.add('is-hidden');
          }
        }
        if (separatorEl) {
          if (hasDiscount) {
            separatorEl.classList.remove('is-hidden');
          } else {
            separatorEl.classList.add('is-hidden');
          }
        }
      }

      if (retractableEl && !isSingleMode) {
        updateGiftBanner(pack.totalPrice || 0);
      }
    }

    var avatarStack = root.querySelector('[data-avatar-stack]');
    var avatarText = root.querySelector('[data-avatar-text]');
    var avatarCaption = root.querySelector('[data-avatar-caption]');
    if (avatarStack && avatarText) {
      var avatars = root.querySelectorAll('[data-avatar-item]');
      var defaultText = avatarText.dataset.defaultText || '';
      var fadeEl = avatarCaption || avatarText;
      var resetTimeout = null;

      function resetAvatars() {
        avatarStack.classList.remove('has-selection');
        avatars.forEach(function(a) {
          a.classList.remove('is-active');
        });
        fadeEl.style.opacity = '0';
        setTimeout(function() {
          avatarText.textContent = defaultText;
          fadeEl.style.opacity = '1';
        }, 200);
      }

      document.addEventListener('click', function(event) {
        if (avatarStack.classList.contains('has-selection') && !avatarStack.contains(event.target)) {
          if (resetTimeout) {
            clearTimeout(resetTimeout);
          }
          resetAvatars();
        }
      });

      avatars.forEach(function(avatar) {
        avatar.addEventListener('click', function() {
          var title = this.dataset.title || '';

          if (resetTimeout) {
            clearTimeout(resetTimeout);
          }

          if (this.classList.contains('is-active')) {
            resetAvatars();
            return;
          }

          avatars.forEach(function(a) {
            a.classList.remove('is-active');
          });
          avatarStack.classList.add('has-selection');
          this.classList.add('is-active');

          fadeEl.style.opacity = '0';
          setTimeout(function() {
            avatarText.textContent = title;
            fadeEl.style.opacity = '1';
          }, 200);

          resetTimeout = setTimeout(resetAvatars, 4000);
        });
      });
    }

    /* --------------------------------------------------------------------
       Bundle "4th pick" option – mutually exclusive with packs 1/2/3.
       Selecting a bundle chip overrides the pack selection: it drives the
       shared coupon price strip + gift banner and swaps the form variant id.
       -------------------------------------------------------------------- */
    var deactivateBundle = null;
    var bundleRoot = root.querySelector('[data-dr-pack-bundle]');

    if (bundleRoot && !isSingleMode) {
      var bundleBox = bundleRoot.querySelector('[data-dr-pack-bundle-box]');
      var bundleChips = Array.prototype.slice.call(bundleRoot.querySelectorAll('[data-bundle-chip]'));
      var bundleDisplays = Array.prototype.slice.call(bundleRoot.querySelectorAll('[data-bundle-display]'));
      var bundleNameEl = bundleRoot.querySelector('[data-bundle-name]');
      var bundleUnitEl = bundleRoot.querySelector('[data-bundle-unit]');
      var bundleActive = false;

      function showBundleOffer(index) {
        bundleDisplays.forEach(function(display) {
          display.classList.toggle('is-active', Number(display.dataset.offerIndex) === index);
        });
        var chip = bundleChips[index];
        if (chip) {
          if (bundleNameEl) bundleNameEl.textContent = chip.dataset.name || '';
          if (bundleUnitEl) bundleUnitEl.textContent = chip.dataset.unit || '';
        }
      }

      function setBundleChipActive(index) {
        bundleChips.forEach(function(chip) {
          var on = Number(chip.dataset.offerIndex) === index;
          chip.classList.toggle('is-active', on);
          chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }

      function updateBundleGift(totalCents) {
        updateGiftBanner(totalCents);
      }

      function selectBundle(index) {
        var chip = bundleChips[index];
        if (!chip) return;

        bundleActive = true;
        radios.forEach(function(radio) { radio.checked = false; });
        idInput.value = chip.dataset.variantId;

        if (bundleBox) bundleBox.classList.add('is-active');
        setBundleChipActive(index);
        showBundleOffer(index);

        // Bundles always use the coupon strip
        if (legacyStripEl) legacyStripEl.classList.add('is-hidden');
        if (couponStripEl) couponStripEl.classList.remove('is-hidden');
        if (couponRegularEl) couponRegularEl.textContent = chip.dataset.regular || '';
        if (couponAmountEl) couponAmountEl.textContent = chip.dataset.coupon || '';
        if (couponUnitEl) couponUnitEl.textContent = chip.dataset.unit || '';
        updateBundleGift(parseInt(chip.dataset.totalCents, 10) || 0);
      }

      deactivateBundle = function() {
        if (!bundleActive) return;
        bundleActive = false;
        if (bundleBox) bundleBox.classList.remove('is-active');
        setBundleChipActive(-1);
      };

      bundleChips.forEach(function(chip, index) {
        chip.addEventListener('click', function(event) {
          event.stopPropagation();
          selectBundle(index);
        });
      });

      // Clicking anywhere in the bundle (outside a specific chip) selects the default (first) bundle
      bundleRoot.addEventListener('click', function() {
        if (!bundleActive) selectBundle(0);
      });

      // Initial: show the first offer's content, but nothing selected (packs stay default)
      showBundleOffer(0);
    }

    radios.forEach(function(radio) {
      radio.addEventListener('change', function() {
        if (this.checked && !isSingleMode) {
          if (deactivateBundle) deactivateBundle();
          applyPack(this.value);
        }
      });
    });

    var checkedRadio = root.querySelector('.dr-pack-option__radio:checked');
    var initialPackKey = (checkedRadio && checkedRadio.value) || config.defaultPack || '1';
    applyPack(initialPackKey);
  }

  function initAllPickers(context) {
    var ctx = context || document;
    var roots = ctx.querySelectorAll('[data-dr-pack-picker]');
    roots.forEach(initPicker);
  }

  window.DRPackPicker = window.DRPackPicker || {};
  window.DRPackPicker.initAll = initAllPickers;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initAllPickers(document);
    });
  } else {
    initAllPickers(document);
  }

  document.addEventListener('shopify:section:load', function(event) {
    if (event.target) {
      initAllPickers(event.target);
    }
  });
})();
